import React from "react";
/**
 * @interface ChipProps
 * @description  사용자 정의 스타일과 동작을 가지는 클릭 가능한 칩(선택/해제 가능) 컴포넌트입니다.
 *
 * @param {ChipProps} props - Chip 컴포넌트에 전달되는 props.
 * @param {React.ReactNode} props.label - 칩 내부에 표시될 라벨 내용.
 * @param {boolean} props.checked - 칩의 현재 선택(체크) 상태.
 * @param {() => void} props.onClick - 칩을 클릭했을 때 실행될 콜백 함수.
 * @param {string} props.name - 내부 input 요소의 name 속성.
 * @param {string} props.id - 칩의 고유 ID이자 내부 input 요소와 label을 연결하는 ID.
 * @param {string} [props.className=""] - 칩에 추가할 사용자 정의 CSS 클래스 이름.
 * @param {object} [props....rest] - 내부 input 요소에 전달될 나머지 표준 HTML 속성들.
 *
 */

interface ChipProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onClick" | "name" | "id" | "checked" | "type" | "className"
  > {
  id: string;
  name: string;
  label: React.ReactNode;
  checked: boolean;
  onClick: () => void;
  variant?: "default" | "primary";
  className?: string;
}

const Chip: React.FC<ChipProps> = ({
  label,
  checked,
  onClick,
  name,
  id,
  variant = "default",
  className = "",
  ...rest
}) => {
  const handleOnClick = (event: React.MouseEvent<HTMLLabelElement>) => {
    event.preventDefault();
    onClick();
  };

  const baseClasses =
    "!inline-flex !m-0 !float-none items-center justify-center w-[101px] h-[36px] rounded-full text-base truncate border border-solid cursor-pointer transition-colors";

  const variantClasses = {
    default: "border-ui-border-strong bg-white text-black",
    checked: "border-black bg-brand-tertiary text-white",
    primary: "border-brand-tertiary bg-ui-surface text-brand-tertiary",
  };

  let currentVariantClass = "";
  if (variant === "default") {
    currentVariantClass = checked ? variantClasses.checked : variantClasses.default;
  } else if (variant === "primary") {
    currentVariantClass = variantClasses.primary;
  }

  return (
    <label
      className={`${baseClasses} ${currentVariantClass} ${className}`}
      onClick={handleOnClick}
      htmlFor={id}
      role="checkbox"
      aria-checked={checked}
    >
      <input id={id} type="checkbox" name={name} readOnly={true} checked={checked} className="sr-only" {...rest} />
      <span>{label}</span>
    </label>
  );
};

export default Chip;
